/**
 * Thin fetch wrappers for the `/api/quote-terms-templates` endpoints.
 *
 * All endpoints are authenticated; admin auth is enforced server-side
 * for the mutating verbs. The list endpoint is open to any logged-in
 * user so the quote preview can hydrate the assigned template.
 */

import type {
  QuoteTermsTemplate,
  QuoteTermsTemplateCreate,
  QuoteTermsTemplateUpdate,
  QuoteTermsTemplatesListResponse,
} from '@techgitter/contracts';

const API_BASE = '/api';

async function unwrap<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `${action} failed: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchQuoteTermsTemplates(
  signal?: AbortSignal,
): Promise<QuoteTermsTemplate[]> {
  const response = await fetch(`${API_BASE}/quote-terms-templates`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await unwrap<QuoteTermsTemplatesListResponse>(
    response,
    'Fetch templates',
  );
  return body.items ?? [];
}

export async function createQuoteTermsTemplate(
  payload: QuoteTermsTemplateCreate,
  signal?: AbortSignal,
): Promise<QuoteTermsTemplate> {
  const response = await fetch(`${API_BASE}/quote-terms-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return unwrap<QuoteTermsTemplate>(response, 'Create template');
}

export async function updateQuoteTermsTemplate(
  id: string,
  payload: QuoteTermsTemplateUpdate,
  signal?: AbortSignal,
): Promise<QuoteTermsTemplate> {
  const response = await fetch(`${API_BASE}/quote-terms-templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return unwrap<QuoteTermsTemplate>(response, 'Update template');
}

export async function deleteQuoteTermsTemplate(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE}/quote-terms-templates/${id}`, {
    method: 'DELETE',
    signal,
  });
  await unwrap<{ deleted: boolean }>(response, 'Delete template');
}

export async function setDefaultQuoteTermsTemplate(
  id: string,
  signal?: AbortSignal,
): Promise<QuoteTermsTemplate> {
  const response = await fetch(
    `${API_BASE}/quote-terms-templates/${id}/set-default`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
    },
  );
  return unwrap<QuoteTermsTemplate>(response, 'Set default template');
}
